import {
  PROJECT_DEVICES_FETCH_STARTED,
  PROJECT_DEVICES_FETCH_SUCCEEDED,
  PROJECT_DEVICES_FETCH_FAILED,
  PROJECT_DEVICES_CHANGE_PAGE,
  PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
  PROJECT_DEVICES_SET_FILTER,
  PROJECT_DEVICES_SET_DEVICE_TYPE,
  PROJECT_DEVICES_SET_FORM_DEVICES
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { getDeviceDiff } from '#component/device/devices-popup/helper';
import { snackBarError } from '#action/fetch';
import { getRequestOptions, getSearchParamsWithoutEmptyFields } from '#helper';

let abortController = new AbortController();

export const fetchCargoDevices = (projectId, exclude, isCreateForm, data = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        page,
        itemsPerPage,
        order,
        orderBy,
        filter,
        filterBy,
        deviceType,
        addMode
      } = getState().projectDevicesPopup;
      const { groupData: { id: groupId } = {}, shipmentId, alertId } = data || {};
      const shipmentCargos = data?.allCargos ?? [];
      const editShipmentForm = {};
      const initialFormValues = data?.initialFormDevices ?? [];
      const currentCargoDevices = data?.formDevices ?? [];

      abortController.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      dispatch({
        type: PROJECT_DEVICES_FETCH_STARTED
      });

      const limit = itemsPerPage;
      const offset = (page - 1) * itemsPerPage;
      const searchParams = new URLSearchParams({
        order,
        orderBy,
        offset,
        limit,
        pagination: 1,
        occupierDetails: 1,
        withoutYoungest: 1
      });

      const openedFromCreateForm = isCreateForm && !shipmentId;

      // Create new shipment.
      // Opened from create form edit mode.
      if (openedFromCreateForm) {
        searchParams.delete('projects');
        const devicesIds = currentCargoDevices?.map(d => d.id).join(',');

        if (!addMode) {
          if (!currentCargoDevices.length) {
            dispatch({
              type: PROJECT_DEVICES_FETCH_SUCCEEDED,
              payload: {
                devices: []
              }
            });
            return;
          }
          searchParams.set('ids', devicesIds);
        } else {
          const addedDevicesIds = currentCargoDevices?.map(d => d.id).join(',');
          searchParams.set('excludeIds', addedDevicesIds);
        }
      }

      // Opened from edit form
      if (isCreateForm && shipmentId) {
        if (!addMode && currentCargoDevices.length === 0) { // No devices in edit mode
          dispatch({
            type: PROJECT_DEVICES_FETCH_SUCCEEDED,
            payload: {
              devices: [],
              alertId
            }
          });
          return;
        }
        const ids = currentCargoDevices.map(gd => gd.id).join(',');
        searchParams.set('ids', ids);
        // If form devices input was changed
        if (currentCargoDevices?.length !== initialFormValues?.length && !addMode) {
          // New devices was added
          if (currentCargoDevices?.length > initialFormValues?.length) {
            const devicesIds = currentCargoDevices.map(d => d.id).join(',');
            searchParams.set('ids', devicesIds);
          } else { // Devices was removed
            const devicesDiff = getDeviceDiff(initialFormValues, currentCargoDevices);
            const excludedDevicesIds = devicesDiff.map(d => d.id).join(',');
            const devicesIds = currentCargoDevices.map(d => d.id).join(',');
            searchParams.set('ids', devicesIds);
            searchParams.set('excludeIds', excludedDevicesIds);
          }
        }
      }

      if (deviceType) {
        if (deviceType.value === 'All types') {
          searchParams.delete('types');
        } else {
          searchParams.set('types', deviceType.value);
        }
      }

      if (filter && typeof filter === 'string') {
        searchParams.set('filterBy', filterBy);
        searchParams.set('filter', filter);
      }

      // Existing shipment only.
      if ((exclude || addMode) && !openedFromCreateForm) {
        searchParams.delete('ids');

        const getAllDevicesIds = (newDevices = []) => {
          let formatted = [];

          if (newDevices.length) {
            formatted = [{ devices: newDevices?.flat() }];
          }

          const allDevices = [...shipmentCargos, ...formatted].reduce((acc, curr) => {
            if (curr.id === groupId) {
              acc.push(...currentCargoDevices);
            } else {
              acc.push(...curr.devices);
            }
            return acc;
          }, []);

          const uniqueIds = [...new Set(allDevices.map(item => item.id))];

          return uniqueIds.join(',');
        };

        let devicesIds = getAllDevicesIds();
        searchParams.set('excludeIds', devicesIds);

        if (shipmentId) {
          // If form devices input was changed
          if (currentCargoDevices?.length !== initialFormValues?.length) {
            // New devices was added
            let devicesDiff;
            if (currentCargoDevices?.length > initialFormValues?.length) {
              devicesDiff = getDeviceDiff(currentCargoDevices, initialFormValues);
            } else { // Devices was removed
              devicesDiff = [];
            }
            devicesIds = getAllDevicesIds(devicesDiff);
            searchParams.set('excludeIds', devicesIds);
          }
        } else {
          devicesIds = getAllDevicesIds();
          searchParams.set('excludeIds', devicesIds);
        }
      }

      // Debounce to do not send multiple requests in short time
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, 200);
      });

      const paramsWithoutEmptyFields = getSearchParamsWithoutEmptyFields(searchParams);
      const url = webPortalApi.buildApiUrl(`device/distinct/details?${paramsWithoutEmptyFields}`);
      const res = await fetch(url, { ...getRequestOptions(), signal });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError('Response is not json');
      }
      if (!res.ok) {
        dispatch(
          snackBarError({
            message: 'Error in request'
          })
        );
      }

      const { devices, last } = await res.json();

      const actionPayload = {
        type: PROJECT_DEVICES_FETCH_SUCCEEDED,
        payload: {
          devices,
          alertId,
          formData: editShipmentForm,
          formDevices: currentCargoDevices,
          exclude
        },
        totalCount: last
      };

      dispatch(actionPayload);
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      dispatch({
        type: PROJECT_DEVICES_FETCH_FAILED,
        error: e
      });
    }
  };

export const setFormData = formDevices => dispatch => {
  try {
    const payload = {
      formDevices
    };

    dispatch({
      type: PROJECT_DEVICES_SET_FORM_DEVICES,
      payload,
      totalCount: formDevices?.length
    });
  } catch (e) {
    // ignore error
  }
};

export const changeOrderAndOrderBy = (
  newOrder,
  newOrderBy,
  projectId,
  addMode,
  isCreateForm,
  applianceData
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });
  dispatch(fetchCargoDevices(projectId, addMode, isCreateForm, applianceData));
};

export const changePage = (
  newPage,
  projectId,
  addMode,
  isCreateForm,
  applianceData,
  refetch = true
) => dispatch => {
  dispatch({
    type: PROJECT_DEVICES_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchCargoDevices(projectId, addMode, isCreateForm, applianceData));
  }
};

export const setFilter = (newFilter, projectId, addMode, isCreateForm, applianceData) =>
  dispatch => {
    dispatch({
      type: PROJECT_DEVICES_SET_FILTER,
      payload: newFilter
    });

    dispatch(fetchCargoDevices(projectId, addMode, isCreateForm, applianceData));
  };

export const setDeviceType = (newType, projectId, addMode, openedFromCreateForm, applianceData) =>
  dispatch => {
    dispatch({
      type: PROJECT_DEVICES_SET_DEVICE_TYPE,
      payload: newType
    });

    dispatch({
      type: PROJECT_DEVICES_CHANGE_PAGE,
      payload: 1
    });

    dispatch(fetchCargoDevices(projectId, addMode, openedFromCreateForm, applianceData));
  };
